// pages/Editor/components/tree/useTreeDerived.ts
import * as React from 'react';
import type { UniqueIdentifier } from '@dnd-kit/core';
import type { AutomationStep, ParentStep } from '@/types/automationSteps';
import type { SiblingParentOption } from './types';
import type { FlattenedStep } from './treeUtils';
import { canHaveChildren } from './treeUtils';

function directChildCount(flat: FlattenedStep[], i: number): number {
  const cur = flat[i];
  const d = cur.depth;
  let count = 0;
  for (let j = i + 1; j < flat.length; j++) {
    const next = flat[j];
    if (next.depth <= d) break; // left this subtree
    if (next.depth === d + 1 && String(next.parentId) === String(cur.id)) count++;
  }
  return count;
}

// (kept) simple local dedupe to be safe; remove if you’re 100% sure upstream never duplicates
function pushUnique(
  arr: SiblingParentOption[],
  seen: Set<string>,
  parentStep: AutomationStep | undefined,
  insertIndex: number,
  afterStep: AutomationStep | undefined,
) {
  const key = `${parentStep?.step_id ?? 'root'}|${insertIndex}`;
  if (!seen.has(key)) {
    arr.push({ parentStep, insertIndex, afterStep });
    seen.add(key);
  }
}

/**
 * meta:
 *  - byId: flattened row lookup
 *  - indexById: zero-based index among a parent’s children
 *  - parentStepsById: ancestor chain (root → immediate parent)
 * addAfterById:
 *  - all valid "add AFTER this row" targets
 */
export function useTreeDerived(flat: FlattenedStep[], activeConditionIds: Record<string, string>) {
  const byId = React.useMemo(() => {
    const m = new Map<string, FlattenedStep>();
    for (const row of flat) m.set(String(row.id), row);
    return m;
  }, [flat]);

  const indexById = React.useMemo(() => {
    const counter = new Map<string, number>(); // key = parentId|'root'
    const map = new Map<UniqueIdentifier, number>();
    for (const row of flat) {
      const key = String(row.parentId ?? 'root');
      const idx = counter.get(key) ?? 0;
      map.set(row.id, idx);
      counter.set(key, idx + 1);
    }
    return map;
  }, [flat]);

  const parentStepsById = React.useMemo(() => {
    const map = new Map<UniqueIdentifier, ParentStep[]>();
    for (const row of flat) {
      const parents: ParentStep[] = [];
      let pid = row.parentId;
      while (pid != null) {
        const pRow = byId.get(String(pid));
        if (!pRow) break;
        // index of this ancestor within its own parent
        const idx = indexById.get(pRow.id) ?? 0;
        parents.unshift({ step: pRow.step, index: idx });
        pid = pRow.parentId;
      }
      map.set(row.id, parents);
    }
    return map;
  }, [flat, byId, indexById]);

  const lastChildById = React.useMemo(() => {
    const seen = new Set<string>();
    const map = new Map<UniqueIdentifier, boolean>();
    for (let i = flat.length - 1; i >= 0; i--) {
      const row = flat[i];
      const key = `${row.parentId ?? 'root'}|${row.depth}`;
      const isLast = row.parentId != null && !seen.has(key);
      map.set(row.id, isLast);
      seen.add(key);
    }
    return map;
  }, [flat]);

  const addAfterById = React.useMemo(() => {
    const map = new Map<UniqueIdentifier, SiblingParentOption[]>();

    for (let i = 0; i < flat.length; i++) {
      const row = flat[i];
      const options: SiblingParentOption[] = [];
      const seen = new Set<string>();

      const isParentCapable = canHaveChildren(row.step, activeConditionIds);
      const childCount = isParentCapable ? directChildCount(flat, i) : 0;
      const hasChildren = childCount > 0;

      // A) INSIDE current row (as child) — always allowed for parent-capable rows
      if (isParentCapable) {
        pushUnique(options, seen, row.step, 0, undefined);
      }

      // B) SAME-LEVEL “after this step”
      //    Only when the row is NOT a parent-capable-with-children.
      if (!(isParentCapable && hasChildren)) {
        if (row.parentId != null) {
          const parent = byId.get(String(row.parentId));
          const curIdx = indexById.get(row.id) ?? 0;
          pushUnique(options, seen, parent?.step, curIdx + 1, row.step);
        } else {
          const curIdx = indexById.get(row.id) ?? 0; // root-level
          pushUnique(options, seen, undefined, curIdx + 1, row.step);
        }
      }

      // C) BUBBLE-UP to ancestors
      //    Also only when the row is NOT a parent-capable-with-children.
      if (!(isParentCapable && hasChildren)) {
        let node: FlattenedStep | undefined = row;
        while (node && node.parentId != null) {
          const isLast = lastChildById.get(node.id) ?? false;
          if (!isLast) break;

          const parent = byId.get(String(node.parentId));
          if (!parent) break;

          const parentIdx = indexById.get(parent.id) ?? 0;
          const grandParent =
            parent.parentId != null ? byId.get(String(parent.parentId))?.step : undefined;

          pushUnique(options, seen, grandParent, parentIdx + 1, parent.step);

          node = parent; // continue bubbling
        }
      }

      map.set(row.id, options);
    }

    return map;
  }, [flat, activeConditionIds, byId, indexById, lastChildById]);

  return { meta: { byId, indexById, parentStepsById }, addAfterById };
}
